import { Id, IsoDate, IsoDateTime, ListQuery, Paginated, QueryVersion, RUN_OUTCOME_MAX_CHARS, defineAction, defineEvent, z } from '@mkcore/shared';
import { agentPermissions as P } from './permissions';

// Namespace agent (docs/01-architectuur/05-api-en-mcp.md, docs/04-domeinen/agents.md): the light registration of
// agents in U3 (CO-1233) and the emergency stop (CO-1234), and AgentRun: the register of every AI call with its
// tokens, costs, decisions and trace (CO-0970, CO-0971, CO-0973), with the monthly AI budget (CO-0972).
// Routines and Hermes runs follow (CO-1250, CO-1270).

export const AGENT_RUNTIMES = ['hermes', 'in_app', 'claude_code', 'cli'] as const;
export const AGENT_STATUSES = ['active', 'paused', 'terminated'] as const;
export const MODEL_ALIASES = ['snel', 'slim', 'vision', 'lokaal'] as const;
export const AUTONOMY_LEVELS = ['propose', 'act'] as const;

const WORKER_LABEL = /^[a-z0-9][a-z0-9-]{0,39}$/;
const Label = z.string().trim().regex(WORKER_LABEL, 'lowercase letters, digits and dashes, at most 40');
const WorkerLabel = Label.nullable().describe(
  'Claude Code and CLI: only a worker with this label runs its sessions; null: only a worker without labels (CO-1367)',
);
const MaxRunMinutes = z
  .number()
  .int()
  .min(5)
  .max(240)
  .nullable()
  .describe('How long one of its runs may take, in minutes; null: 30. The timeout of a routine goes before it (CO-1374)');

export const Agent = z
  .object({
    id: Id,
    principalId: Id.describe('The identity of the agent: its roles, grants and tokens'),
    name: z.string(),
    role: z.string().describe('What the agent is, such as time assistant; also used for routing'),
    description: z.string().describe('Its job in a few sentences'),
    runtime: z.enum(AGENT_RUNTIMES),
    profile: z.string().nullable().describe('The name of its profile on the Hermes instance'),
    definition: z.string().nullable().describe('runtime in_app: the definition in the code of the app it runs (CO-0834)'),
    modelAlias: z.enum(MODEL_ALIASES).nullable(),
    instructions: z.string().describe('Persona and rules in markdown, in the style of AGENTS.md'),
    defaultFor: z.array(z.string()).describe('Domains for which it is the default agent, such as time'),
    approvalTools: z.array(z.string()).describe('Tools that wait for a person, besides every external and destructive tool (CO-0930)'),
    autoApproveTools: z.array(z.string()).describe('External tools that do not wait for a person, written with its own Forgejo bot account; never a destructive tool (#1489, ADR-0046)'),
    mcpTools: z.array(z.string()).describe('Tools of external MCP servers it may call, as server/tool or server/* (CO-0838)'),
    heartbeatMinutes: z.number().int().nullable().describe('Wake every so many minutes to pick up work; null: only on a reason (CO-1240)'),
    maxConcurrentRuns: z.number().int().describe('Runs at the same time; wakes wait while it is busy (CO-1240)'),
    autonomy: z.enum(AUTONOMY_LEVELS).describe('propose: every write waits for a person; act: only external, destructive and approvalTools wait (CO-1247)'),
    maxActionsBeforeApproval: z.number().int().nullable().describe('After so many writes in one run the next one waits for a person (CO-1247)'),
    managerId: Id.nullable().describe('The person or agent it reports to (CO-1254)'),
    managerName: z.string().nullable(),
    knowledgeSpaces: z.array(Id).describe('The knowledge spaces it may read besides its own; empty: what its grants allow (CO-1251)'),
    instructionsPath: z.string().nullable().describe('owner/repo/path of a markdown file on the default branch that replaces the instructions from the next run (CO-1258)'),
    workerLabel: WorkerLabel,
    maxRunMinutes: MaxRunMinutes,
    revision: z.number().int().describe('The number of its current configuration (CO-1258)'),
    externalRef: z.string().nullable().describe('Where it came from, such as paperclip:<id>: a second import finds it (CO-1253)'),
    status: z.enum(AGENT_STATUSES),
    statusReason: z.string().nullable(),
    statusAt: IsoDateTime.nullable(),
    statusByName: z.string().nullable(),
    roleKeys: z.array(z.string()),
    createdAt: IsoDateTime,
    version: z.number().int(),
  })
  .openapi('Agent');

const Role = z.string().trim().min(1).max(120);
const Description = z.string().max(4000);
const Runtime = z.enum(AGENT_RUNTIMES);
const Profile = z.string().trim().max(120).nullable();
const Definition = z.string().trim().max(80).nullable().describe('runtime in_app: the name of a definition from agent.definition.list');
const Alias = z.enum(MODEL_ALIASES).nullable();
const Instructions = z.string().max(50_000);
const DefaultFor = z.array(z.string().max(40)).max(20);
const ApprovalTools = z.array(z.string().max(120)).max(100).describe('Tool names or patterns such as crm.* or time.entry.create');
const AutoApproveTools = z.array(z.string().max(120)).max(100).describe('External tool names or patterns such as dev.issue.comment that run without a person (ADR-0046)');
/** server/tool or server/* (CO-0838): tools of external MCP servers an in-app agent may call. */
export const MCP_TOOL_PATTERN = /^[a-z][a-z0-9]{1,29}\/([A-Za-z0-9_.-]{1,100}|\*)$/;
const McpTools = z
  .array(z.string().regex(MCP_TOOL_PATTERN, 'server/tool or server/*'))
  .max(100)
  .describe('runtime in_app: tools of external MCP servers it may call, as server/tool or server/* (CO-0838); only tools an admin turned on');
const HeartbeatMinutes = z.number().int().min(5).max(1440).nullable().describe('Wake every so many minutes; null: only on a reason (CO-1240)');
const MaxConcurrentRuns = z.number().int().min(1).max(10);
const Autonomy = z.enum(AUTONOMY_LEVELS);
const ExternalRef = z.string().trim().min(1).max(200).nullable().describe('A reference in the system it came from, such as paperclip:<id>; unique (CO-1253)');
const MaxActions = z.number().int().min(1).max(1000).nullable();
const ManagerId = Id.nullable().describe('A person or another agent (its principal id)');
const KnowledgeSpaces = z.array(Id).max(50).describe('Knowledge space ids; empty: what its grants allow');
const InstructionsPath = z
  .string()
  .trim()
  .max(300)
  .regex(/^[\w.-]+\/[\w.-]+\/.+\.md$/, 'owner/repo/path/to/file.md')
  .nullable()
  .describe('owner/repo/path/to/file.md on the default branch of a mirrored repository');

/** What a revision holds (CO-1258): everything that steers the agent, without its name, roles and status. */
export const AgentConfig = z
  .object({
    role: Role,
    description: Description,
    runtime: Runtime,
    profile: Profile,
    definition: z.string().trim().max(80).nullable(),
    modelAlias: Alias,
    instructions: Instructions,
    instructionsPath: InstructionsPath,
    workerLabel: WorkerLabel,
    defaultFor: DefaultFor,
    approvalTools: ApprovalTools,
    autoApproveTools: AutoApproveTools.default([]),
    mcpTools: McpTools.default([]),
    heartbeatMinutes: HeartbeatMinutes,
    maxConcurrentRuns: MaxConcurrentRuns,
    maxRunMinutes: MaxRunMinutes,
    autonomy: Autonomy,
    maxActionsBeforeApproval: MaxActions,
    managerId: ManagerId,
    knowledgeSpaces: KnowledgeSpaces,
  })
  .openapi('AgentConfig');

export const listAgents = defineAction({
  name: 'agent.agent.list',
  method: 'get',
  path: '/agent/agents',
  permission: P.keys.read,
  input: z.object({ status: z.enum(AGENT_STATUSES).optional(), externalRef: z.string().max(200).optional().describe('The agent with this reference, such as paperclip:<id>') }),
  output: z.object({ items: z.array(Agent) }),
  summary: 'The agents of the company with their role, runtime and status (CO-1233)',
});

export const getAgent = defineAction({
  name: 'agent.agent.get',
  method: 'get',
  path: '/agent/agents/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: Agent,
  summary: 'One agent with its instructions',
});

export const agentProfile = defineAction({
  name: 'agent.agent.profile',
  method: 'get',
  path: '/agent/agents/{id}/profile',
  permission: P.keys.update,
  input: z.object({ id: Id }),
  output: z.object({
    profile: z.string().nullable().describe('The profile on the Hermes instance'),
    tools: z.array(z.string()).nullable().describe('The tools of its tokens spelled out; null: every tool'),
    envVar: z.string().describe('The environment variable on the Hermes VM that holds the token'),
    config: z.string().describe('The mcp_servers block for the profile (YAML), without the token'),
  }),
  summary: 'The MCP connection of an agent for its Hermes profile, with the same tool subset as its token (CO-0829)',
  description: 'For the rollout of a profile (runbook Hermes bijwerken). The token itself never appears: the block names the environment variable. Needs a token on the agent.',
});

export const createAgent = defineAction({
  name: 'agent.agent.create',
  method: 'post',
  path: '/agent/agents',
  permission: P.keys.create,
  input: z
    .object({
      name: z.string().trim().min(1).max(100),
      roleKeys: z.array(z.string()).min(1).max(10).default(['agent']).describe('Roles of the identity; never a system role, and approve is always taken away'),
      role: Role,
      description: Description.default(''),
      runtime: Runtime.default('hermes'),
      profile: Profile.default(null),
      definition: Definition.default(null),
      modelAlias: Alias.default(null),
      instructions: Instructions.default(''),
      defaultFor: DefaultFor.default([]),
      approvalTools: ApprovalTools.default([]),
      autoApproveTools: AutoApproveTools.default([]),
      mcpTools: McpTools.default([]),
      heartbeatMinutes: HeartbeatMinutes.default(null),
      maxConcurrentRuns: MaxConcurrentRuns.default(1),
      maxRunMinutes: MaxRunMinutes.default(null),
      autonomy: Autonomy.default('act'),
      maxActionsBeforeApproval: MaxActions.default(null),
      managerId: ManagerId.default(null),
      knowledgeSpaces: KnowledgeSpaces.default([]),
      instructionsPath: InstructionsPath.default(null),
      workerLabel: WorkerLabel.default(null),
      externalRef: ExternalRef.default(null),
    })
    .strict(),
  output: Agent,
  summary: 'Registers an agent: its identity with roles and its profile, in one go (CO-1233)',
  description: 'Needs tenant-wide scope. Give it a token afterwards with admin.token.create (principalId).',
});

export const updateAgent = defineAction({
  name: 'agent.agent.update',
  method: 'patch',
  path: '/agent/agents/{id}',
  permission: P.keys.update,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      expectedVersion: z.number().int().positive().optional(),
      role: Role.optional(),
      description: Description.optional(),
      runtime: Runtime.optional(),
      profile: Profile.optional(),
      definition: Definition.optional(),
      modelAlias: Alias.optional(),
      instructions: Instructions.optional(),
      defaultFor: DefaultFor.optional(),
      approvalTools: ApprovalTools.optional(),
      autoApproveTools: AutoApproveTools.optional(),
      mcpTools: McpTools.optional(),
      heartbeatMinutes: HeartbeatMinutes.optional(),
      maxConcurrentRuns: MaxConcurrentRuns.optional(),
      maxRunMinutes: MaxRunMinutes.optional(),
      autonomy: Autonomy.optional(),
      maxActionsBeforeApproval: MaxActions.optional(),
      managerId: ManagerId.optional(),
      knowledgeSpaces: KnowledgeSpaces.optional(),
      instructionsPath: InstructionsPath.optional(),
      workerLabel: WorkerLabel.optional(),
      externalRef: ExternalRef.optional(),
      note: z.string().trim().max(500).optional().describe('Why, on the revision (CO-1258)'),
    })
    .strict(),
  output: Agent,
  summary: 'Changes the configuration of an agent; every change is a revision (CO-1233, CO-1258)',
});

const stopInput = z.object({ id: Id, reason: z.string().trim().max(500).default('') }).strict();

export const pauseAgent = defineAction({
  name: 'agent.agent.pause',
  method: 'post',
  path: '/agent/agents/{id}/pause',
  permission: P.keys.update,
  successStatus: 200,
  input: stopInput,
  output: Agent,
  summary: 'Pauses an agent at once: its tokens stop working until someone resumes it (CO-1234)',
});

export const pauseAllAgents = defineAction({
  name: 'agent.agent.pauseAll',
  method: 'post',
  path: '/agent/agents/pause-all',
  permission: P.keys.update,
  successStatus: 200,
  input: z.object({ reason: z.string().trim().max(500).default('') }).strict(),
  output: z.object({ paused: z.number().int() }),
  summary: 'The emergency stop: pauses every active agent at once (CO-1234)',
});

export const resumeAgent = defineAction({
  name: 'agent.agent.resume',
  method: 'post',
  path: '/agent/agents/{id}/resume',
  permission: P.keys.update,
  successStatus: 200,
  input: z.object({ id: Id }).strict(),
  output: Agent,
  summary: 'Resumes a paused agent',
});

export const terminateAgent = defineAction({
  name: 'agent.agent.terminate',
  method: 'post',
  path: '/agent/agents/{id}/terminate',
  permission: P.keys.manage,
  successStatus: 200,
  input: stopInput,
  output: Agent,
  kind: 'destructive',
  summary: 'Ends an agent for good; its identity stays for the history (audit, hours, costs)',
});

// ---------------------------------------------------------------- the Hermes profile in sync (CO-1384, ADR-0037)

export const PROFILE_SYNC_STATES = ['pending', 'in_sync', 'error', 'archived'] as const;

export const ProfileSync = z
  .object({
    agentId: Id,
    profile: z.string().nullable().describe('The profile on the Hermes instance this state is about'),
    state: z.enum(PROFILE_SYNC_STATES).describe('pending: a sync is on its way; in_sync: the provisioner wrote what the app holds; error: the last sync failed; archived: the agent ended and its profile went to the archive'),
    detail: z.string().describe('What the last sync or check found, such as the error'),
    syncedAt: IsoDateTime.nullable().describe('When the provisioner last wrote the profile'),
    checkedAt: IsoDateTime.nullable().describe('When the check every 15 minutes last compared it'),
  })
  .openapi('AgentProfileSync');

export const agentProfileSync = defineAction({
  name: 'agent.agent.profileSync',
  method: 'get',
  path: '/agent/agents/{id}/profile/sync',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: ProfileSync,
  summary: 'Whether the Hermes profile of an agent is in sync with the app (CO-1384)',
  description: 'state pending without syncedAt: the profile was never written. No provisioner configured (HERMES_PROVISIONER_URL): state error with the reason.',
});

export const syncAgentProfile = defineAction({
  name: 'agent.agent.syncProfile',
  method: 'post',
  path: '/agent/agents/{id}/profile/sync',
  permission: P.keys.update,
  successStatus: 202,
  input: z.object({ id: Id }).strict(),
  output: ProfileSync,
  summary: 'Writes the Hermes profile of an agent again through the provisioner (CO-1384)',
  description: 'Makes its token and key when they are missing and writes model, tools and instructions to the profile. Runs in the background: read agent.agent.profileSync for the outcome.',
});

export const DELIVERY_STATES = ['none', 'delivered', 'failed'] as const;
export const RUN_RUNTIMES = ['in_process', 'hermes', 'in_app', 'claude_code', 'cli'] as const;
export const RUN_STATUSES = ['running', 'succeeded', 'failed', 'cancelled'] as const;
export const RUN_TRIGGERS = ['event', 'schedule', 'manual', 'assignment', 'check'] as const;
/** Why a run ended without its work: not_runnable, the preflight found what makes it fail (CO-1400); skipped, its precheck found nothing to do (CO-1397). */
export const RUN_END_REASONS = ['not_runnable', 'skipped', 'over_limit'] as const;
export const RUN_EVENT_KINDS = ['message', 'tool_call', 'tool_result', 'knowledge_read', 'knowledge_write', 'error', 'status'] as const;
export const CALL_KINDS = ['generate', 'decide', 'run'] as const;
export const CALL_STATUSES = ['ok', 'invalid', 'error'] as const;
export const COST_GROUPS = ['agent', 'task', 'model', 'project', 'engagement', 'organisation'] as const;

/** Limits of one run (CO-0837); over a limit the run fails with agent-run-limit. */
export const RunLimits = z
  .object({
    maxCalls: z.number().int().positive().optional().describe('Model calls'),
    maxTokens: z.number().int().positive().optional().describe('Input plus output tokens'),
    maxCostMicros: z.number().int().positive().optional().describe('Costs in millionths of the currency'),
    maxDurationMs: z.number().int().positive().optional().describe('From the start of the run'),
    maxToolCalls: z.number().int().positive().optional(),
  })
  .openapi('RunLimits');

const Costs = {
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  costMicros: z.number().int().describe('AI costs in millionths of the currency'),
  currency: z.string(),
};

export const AgentRun = z
  .object({
    id: Id,
    agentId: Id.nullable(),
    routineId: Id.nullable(),
    routineName: z.string().nullable(),
    agentName: z.string().nullable().describe('Null: an in-process job without an agent'),
    agentRevision: z.number().int().nullable().describe('The revision of the agent when the run started (CO-0853)'),
    runtime: z.enum(RUN_RUNTIMES),
    task: z.string().describe('What the run does, such as project.weeklyStatus.summarize'),
    trigger: z.enum(RUN_TRIGGERS),
    status: z.enum(RUN_STATUSES),
    workflowId: z.string().nullable(),
    targetType: z.string().nullable(),
    targetId: Id.nullable(),
    projectId: Id.nullable(),
    issueId: Id.nullable(),
    engagementId: Id.nullable(),
    organisationId: Id.nullable(),
    onBehalfOfName: z.string().nullable(),
    startedAt: IsoDateTime,
    finishedAt: IsoDateTime.nullable(),
    durationMs: z.number().int().nullable(),
    calls: z.number().int(),
    toolCalls: z.number().int(),
    estimatedTokens: z.number().int().describe('A Hermes run: its tokens estimated from its MCP tool results while it runs (#1286); the usage at its end is in inputTokens and outputTokens'),
    ...Costs,
    outcome: z.string().nullable().describe('The final answer or result: whole in agent.run.get (up to 200,000 characters), its first 4000 characters in a list'),
    outcomeTruncated: z.boolean().describe('The list shows the start of a longer outcome; agent.run.get has all of it'),
    error: z.string().nullable(),
    externalRunId: z.string().nullable().describe('The run id at the runtime, such as Hermes'),
    deliveryState: z.enum(DELIVERY_STATES).describe('Whether the result reached its target (CO-0731), apart from the run status'),
    deliveryError: z.string().nullable(),
    endReason: z
      .enum(RUN_END_REASONS)
      .nullable()
      .describe('not_runnable: the preflight found what makes the run fail, so it did not start and cost nothing (CO-1400); skipped: its precheck found nothing to do (CO-1397); over_limit: the run answered, but the usage it reported at its end went over a limit (#1438)'),
  })
  .openapi('AgentRun');

export const AgentRunCall = z
  .object({
    id: Id,
    kind: z.enum(CALL_KINDS),
    task: z.string(),
    alias: z.enum(MODEL_ALIASES).nullable(),
    provider: z.string(),
    modelId: z.string(),
    status: z.enum(CALL_STATUSES),
    error: z.string().nullable(),
    durationMs: z.number().int(),
    at: IsoDateTime,
    ...Costs,
  })
  .openapi('AgentRunCall');

export const DecisionAnswer = z.object({
  value: z.union([z.string(), z.number(), z.boolean()]),
  probabilities: z.record(z.string(), z.number()).nullable(),
  confidence: z.number().min(0).max(1),
});

export const AgentRunDecision = z
  .object({
    id: Id,
    task: z.string(),
    engine: z.enum(['jev', 'llm']),
    questions: z.record(z.string(), z.unknown()),
    answers: z.record(z.string(), DecisionAnswer),
    confidence: z.number().min(0).max(1).describe('The lowest confidence of the answers'),
    threshold: z.number(),
    path: z.string().nullable().describe('The path the code chose, such as auto or human'),
    at: IsoDateTime,
  })
  .openapi('AgentRunDecision');

export const AgentRunEvent = z
  .object({
    id: Id,
    seq: z.number().int(),
    kind: z.enum(RUN_EVENT_KINDS),
    name: z.string().nullable().describe('Tool name or subject'),
    data: z.unknown().describe('Masked: secrets and sensitive fields never land here (CO-0963)'),
    at: IsoDateTime,
  })
  .openapi('AgentRunEvent');

/** What a person did with a draft of an agent (CO-0977, CO-0853): revised is "almost, do it again", rejected is "no". */
export const FEEDBACK_LABELS = ['approved', 'changed', 'revised', 'rejected'] as const;
export const RunFeedback = z
  .object({
    id: Id,
    runId: Id,
    approvalId: Id,
    kind: z.string().nullable().describe('The approval kind, such as agent.tool or support-reply'),
    agentRevision: z.number().int().nullable().describe('The revision of the agent that made the draft (CO-0853)'),
    label: z.enum(FEEDBACK_LABELS),
    tool: z.string().nullable(),
    correction: z
      .object({ before: z.record(z.string(), z.unknown()), after: z.record(z.string(), z.unknown()) })
      .nullable()
      .describe('changed: the arguments or the draft of the agent, and as approved; null when the domain only knows that it changed'),
    comment: z.string(),
    reasonCode: z.string().nullable().describe('rejected and revised: the reason from the choice list approval.decisionReason, such as wrong-content (CO-1393)'),
    decidedBy: Id.nullable(),
    decidedByName: z.string().nullable(),
    at: IsoDateTime,
  })
  .openapi('AgentRunFeedback');

export const AgentRunDetail = AgentRun.extend({
  limits: RunLimits,
  runCalls: z.array(AgentRunCall),
  decisions: z.array(AgentRunDecision),
  events: z.array(AgentRunEvent),
  feedback: z.array(RunFeedback).describe('What people did with the drafts of this run (CO-0977)'),
}).openapi('AgentRunDetail');

/** One decided draft with what led to it (CO-0853): a case for an evaluation set or a better prompt. */
export const FeedbackExample = z
  .object({
    feedback: RunFeedback,
    run: z.object({ id: Id, agentId: Id.nullable(), agentName: z.string().nullable(), agentRevision: z.number().int().nullable(), task: z.string(), targetType: z.string().nullable(), targetId: Id.nullable(), startedAt: IsoDateTime }),
    prompt: z.string().nullable().describe('What the run was asked, as recorded at its start (masked, at most 4000 characters)'),
    request: z.object({ title: z.string(), summary: z.string() }).describe('What the approval request said'),
  })
  .openapi('AgentFeedbackExample');

export const exportFeedback = defineAction({
  name: 'agent.feedback.export',
  method: 'get',
  path: '/agent/feedback',
  permission: P.keys.read,
  input: ListQuery.extend({
    from: IsoDate.optional().describe('Decisions from this day'),
    to: IsoDate.optional().describe('Decisions up to and including this day'),
    agentId: Id.optional(),
    agentRevision: z.coerce.number().int().min(1).optional().describe('With agentId: one version of its instructions'),
    label: z.enum(FEEDBACK_LABELS).optional(),
    kind: z.string().max(100).optional().describe('The approval kind, such as agent.tool or support-reply'),
    reasonCode: z.string().max(100).optional().describe('Only rejected or revised drafts with this reason, such as wrong-content (CO-1393)'),
  }),
  output: Paginated(FeedbackExample),
  summary: 'The drafts of agents that people decided on, with the prompt, the request, the label, the correction and the comment (CO-0853)',
  description: 'Newest decision first. The raw material for an evaluation set and for better instructions: changed and revised cases show what the agent should have done.',
});

export const RUN_METRICS_BY =['agent', 'revision', 'model', 'trigger', 'day'] as const;
/** What people did with the drafts of these runs (CO-0853), by the start of the run. */
const FeedbackFigures = z.object({
  approved: z.number().int(),
  changed: z.number().int(),
  revised: z.number().int(),
  rejected: z.number().int(),
  acceptedBp: z.number().int().nullable().describe('Approved without a change, of all decided drafts, in basis points'),
  reasons: z.record(z.string(), z.number().int()).describe('Rejected and revised drafts per reason of approval.decisionReason (CO-1393); without the ones from before the reason'),
});
const RunFigures = z.object({
  runs: z.number().int(),
  succeeded: z.number().int(),
  failed: z.number().int(),
  cancelled: z.number().int(),
  running: z.number().int(),
  successBp: z.number().int().nullable().describe('Succeeded of the finished runs, in basis points'),
  medianMs: z.number().int().nullable(),
  p90Ms: z.number().int().nullable(),
  costMicros: z.number().int(),
  feedback: FeedbackFigures.nullable().describe('Null for by=model: a draft is of the run, not of one model call'),
});

export const runMetrics = defineAction({
  name: 'agent.run.metrics',
  method: 'get',
  path: '/agent/run-metrics',
  permission: P.keys.read,
  input: z.object({ from: IsoDate, to: IsoDate, by: z.enum(RUN_METRICS_BY).default('agent') }),
  output: z.object({
    from: IsoDate,
    to: IsoDate,
    by: z.enum(RUN_METRICS_BY),
    currency: z.string().describe('AI_CURRENCY'),
    items: z.array(RunFigures.extend({ key: z.string(), label: z.string().nullable(), budgetMicros: z.number().int().nullable().describe('by=agent: its monthly budget') })),
    totals: RunFigures,
  }),
  summary: 'Agent runs per agent, revision of an agent, model, trigger or day: how they ended, success rate, duration, AI cost against the budget, and what people did with their drafts (CO-0975, CO-0853)',
  description:
    'Per model it counts the runs that called that model, with the cost of those calls. by=revision compares the versions of the instructions of an agent. ' +
    'feedback counts the drafts people approved, changed, sent back for a revision or rejected. For the insights page and the hourly run alerts (CO-0976).',
});


export const listRuns = defineAction({
  name: 'agent.run.list',
  method: 'get',
  path: '/agent/runs',
  permission: P.keys.read,
  input: ListQuery.extend({
    status: z.enum(RUN_STATUSES).optional().describe('failed shows the runs that went wrong'),
    agentId: Id.optional(),
    routineId: Id.optional(),
    task: z.string().max(120).optional().describe('Exact task or a prefix ending with *'),
    targetId: Id.optional().describe('Runs about this issue, project, engagement or organisation'),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    feedback: z.enum([...FEEDBACK_LABELS, 'any']).optional().describe('Runs whose draft a person approved, rejected or changed (CO-0977)'),
  }),
  output: Paginated(AgentRun),
  summary: 'Agent runs, newest first, with status, tokens and costs (CO-0970, CO-0971)',
});

export const getRun = defineAction({
  name: 'agent.run.get',
  method: 'get',
  path: '/agent/runs/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: AgentRunDetail,
  summary: 'One run with its model calls, decisions with their chances, and its trace (CO-0971, CO-0973)',
  description: 'The changes the run made are in the audit trail: admin.auditLog.list with agentRunId (CO-0969).',
});

export const CostRow = z.object({ key: z.string().nullable(), label: z.string(), runs: z.number().int(), calls: z.number().int(), ...Costs });

export const summarizeCosts = defineAction({
  name: 'agent.run.costs',
  method: 'get',
  path: '/agent/costs',
  permission: P.keys.read,
  input: z.object({
    groupBy: z.enum(COST_GROUPS).default('agent'),
    from: IsoDateTime.optional().describe('Inclusive; default the start of this month'),
    to: IsoDateTime.optional().describe('Exclusive; default now'),
  }),
  output: z.object({ items: z.array(CostRow), total: CostRow, from: IsoDateTime, to: IsoDateTime }),
  summary: 'AI costs and tokens of a period, per agent, task, model, project, engagement or organisation (CO-0970)',
});

export const AiBudget = z
  .object({
    monthlyLimitMicros: z.number().int().nullable().describe('Null: no budget'),
    currency: z.string(),
    month: z.string().describe('YYYY-MM'),
    spentMicros: z.number().int(),
    projectedMicros: z.number().int().describe('Spend at the end of the month at the burn rate so far'),
    percent: z.number().int().nullable(),
    alertedPercent: z.number().int().nullable().describe('The highest alert sent this month: 50, 75, 90 or 100'),
    stoppedAt: IsoDateTime.nullable().describe('Set at 100%: automatic runs wait until someone resets it'),
    version: z.number().int(),
  })
  .openapi('AiBudget');

export const getBudget = defineAction({
  name: 'agent.budget.get',
  method: 'get',
  path: '/agent/budget',
  permission: P.keys.read,
  input: z.object({}),
  output: AiBudget,
  summary: 'The monthly AI budget with the spend so far and the burn rate (CO-0972)',
});

export const setBudget = defineAction({
  name: 'agent.budget.set',
  method: 'put',
  path: '/agent/budget',
  permission: P.keys.manage,
  successStatus: 200,
  input: z.object({ monthlyLimitMicros: z.number().int().positive().nullable(), expectedVersion: z.number().int().positive().optional() }).strict(),
  output: AiBudget,
  summary: 'Sets the monthly AI budget; alerts at 50, 75, 90 and 100%, and a stop of automatic runs at 100%',
});

export const resetBudget = defineAction({
  name: 'agent.budget.reset',
  method: 'post',
  path: '/agent/budget/reset',
  permission: P.keys.manage,
  successStatus: 200,
  input: z.object({ reason: z.string().trim().min(1).max(500) }).strict(),
  output: AiBudget,
  summary: 'Lifts the stop after the budget was used up: automatic runs start again this month',
});

export const ROUTINE_TRIGGERS = ['cron', 'interval', 'once', 'event'] as const;
export const ROUTINE_STATUSES = ['active', 'paused'] as const;
export const ROUTINE_TARGETS = ['none', 'notify', 'mail_draft'] as const;
/** How target notify delivers the result (CO-1392): as the routine result, or as a brief at once by mail and chat. */
export const ROUTINE_DELIVERIES = ['result', 'brief'] as const;
export const CATCH_UP = ['once', 'skip'] as const;
export const PRECHECK_CONDITIONS = ['nonEmpty', 'changedSinceLastRun'] as const;

const Cron = z
  .string()
  .trim()
  .regex(/^\S+(\s+\S+){4}$/, 'Five fields: minute hour day month weekday')
  .describe('Cron in five fields, such as 0 7 * * 1-5');

export const Routine = z
  .object({
    id: Id,
    name: z.string(),
    instruction: z.string().describe('What the agent does, in markdown'),
    agentId: Id.nullable().describe('Null: the default agent of the domain'),
    agentName: z.string().nullable().describe('The agent that runs it now: its own, or the default of the domain'),
    domain: z.string().nullable().describe('For the default agent, such as time'),
    trigger: z.enum(ROUTINE_TRIGGERS),
    cron: z.string().nullable(),
    timezone: z.string(),
    intervalMinutes: z.number().int().nullable(),
    runAt: IsoDateTime.nullable(),
    eventType: z.string().nullable(),
    eventFilter: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).describe('Payload fields that must match'),
    agentEvents: z.boolean().describe('Also start on events an agent caused, never those of its own agent (CO-1249)'),
    maxRunsPerHour: z.number().int().describe('At most this many event runs per hour; more events of agents are skipped with a notification, more of people or the system wait for room'),
    toolAllowlist: z.array(z.string()).nullable().describe('The tools the run may use; null: those of the agent token'),
    gate: z.array(z.string()).describe('What always goes through the approval inbox, such as billing.invoice'),
    target: z.enum(ROUTINE_TARGETS),
    targetUserIds: z.array(Id),
    deliverAs: z
      .enum(ROUTINE_DELIVERIES)
      .describe('target notify: result (agent.routine.result, in the daily digest by default) or brief (agent.routine.brief: at once by mail and Hermes chat, never in the digest, never grouped) (CO-1392)'),
    chat: z.boolean().describe('target notify: the result also through Hermes chat to each recipient with a chat address, whatever their chat setting for the kind (#1347)'),
    mailTo: z.array(z.string()).describe('target mail_draft: the recipients of the draft; empty is the maker'),
    catchUp: z.enum(CATCH_UP),
    repository: z.string().nullable().describe('owner/repo: a Claude Code or CLI agent works in a clone of it (CO-1361)'),
    branch: z.string().nullable().describe('The branch to clone; null: the default branch'),
    maxTurns: z.number().int().nullable().describe('At most this many turns of Claude Code (CO-1361)'),
    timeoutMinutes: z.number().int().nullable().describe('The longest a run may take; null: the default of 30 minutes'),
    precheck: z
      .object({ tool: z.string(), input: z.record(z.string(), z.unknown()), condition: z.enum(PRECHECK_CONDITIONS) })
      .nullable()
      .describe('Null: every run starts. Else an automatic run starts only when this read tool finds something to do (CO-1397)'),
    pauseAfterFailures: z.number().int().describe('The app pauses the routine after this many failed or not runnable runs in a row (CO-1401)'),
    status: z.enum(ROUTINE_STATUSES),
    statusReason: z.string().nullable().describe('Why the app paused it, such as after five failed runs in a row (CO-1401); null when a person did'),
    nextFireAt: IsoDateTime.nullable(),
    lastFiredAt: IsoDateTime.nullable(),
    lastRun: z.object({ id: Id, status: z.enum(RUN_STATUSES), startedAt: IsoDateTime }).nullable(),
    createdAt: IsoDateTime,
    version: z.number().int(),
  })
  .openapi('Routine');

/**
 * The precheck of a routine (CO-1397): one read tool with its input, called with the rights of the agent before an
 * automatic run. nonEmpty: the run starts only when the tool returns something (a list with items); changedSinceLastRun:
 * only when its result differs from that at the last run that succeeded.
 */
export const RoutinePrecheck = z
  .object({
    tool: z
      .string()
      .regex(/^[a-z][a-zA-Z0-9_]*(?:\.[a-z][a-zA-Z0-9_]*){2,}$/, 'A tool name such as dev.issue.list')
      .describe('A tool that only reads, such as dev.issue.list'),
    input: z.record(z.string(), z.unknown()).default({}).describe('The input of the tool, as for a call'),
    condition: z.enum(PRECHECK_CONDITIONS),
  })
  .strict()
  .openapi('RoutinePrecheck');

export const FINDING_SEVERITIES = ['error', 'warning'] as const;

/** One finding of the preflight of a routine (CO-1400): an error stops a run before it starts, a warning does not. */
export const RoutineFinding = z
  .object({
    code: z.string().describe('Such as tool-missing, profile-key-missing, profile-not-in-sync or forgejo-bot-missing'),
    severity: z.enum(FINDING_SEVERITIES).describe('error: a run does not start; warning: worth a look'),
    message: z.string(),
  })
  .openapi('RoutineFinding');

const SavedRoutine = Routine.extend({
  findings: z.array(RoutineFinding).describe('The preflight (CO-1400): what would stop or hinder a run; the routine is saved all the same'),
}).openapi('SavedRoutine');

const routineFields = {
  instruction: z.string().trim().min(1).max(20_000),
  agentId: Id.nullable(),
  domain: z.string().trim().max(40).nullable(),
  trigger: z.enum(ROUTINE_TRIGGERS),
  cron: Cron.nullable(),
  timezone: z.string().min(1).max(64),
  intervalMinutes: z.number().int().min(5).max(60 * 24 * 31).nullable(),
  runAt: IsoDateTime.nullable(),
  eventType: z.string().max(100).nullable(),
  eventFilter: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  agentEvents: z.boolean(),
  maxRunsPerHour: z.number().int().min(1).max(100),
  toolAllowlist: z.array(z.string().max(120)).max(100).nullable(),
  gate: z.array(z.string().max(80)).max(30),
  target: z.enum(ROUTINE_TARGETS),
  targetUserIds: z.array(Id).max(20),
  deliverAs: z.enum(ROUTINE_DELIVERIES),
  chat: z.boolean(),
  mailTo: z.array(z.email()).max(20),
  catchUp: z.enum(CATCH_UP),
  repository: z.string().trim().max(200).regex(/^[\w.-]+\/[\w.-]+$/, 'owner/repo').nullable(),
  branch: z.string().trim().min(1).max(200).nullable(),
  maxTurns: z.number().int().min(1).max(500).nullable(),
  timeoutMinutes: z.number().int().min(1).max(24 * 60).nullable(),
  precheck: RoutinePrecheck.nullable(),
  pauseAfterFailures: z.number().int().min(1).max(100),
};

export const listRoutines = defineAction({
  name: 'agent.routine.list',
  method: 'get',
  path: '/agent/routines',
  permission: P.keys.read,
  input: z.object({ status: z.enum(ROUTINE_STATUSES).optional(), agentId: Id.optional() }),
  output: z.object({ items: z.array(Routine) }),
  summary: 'The routines of the company: instruction, trigger, agent, next fire and last run (CO-1250)',
});

export const getRoutine = defineAction({
  name: 'agent.routine.get',
  method: 'get',
  path: '/agent/routines/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: Routine,
  summary: 'One routine',
});

export const checkRoutine = defineAction({
  name: 'agent.routine.check',
  method: 'get',
  path: '/agent/routines/{id}/check',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: z.object({ routineId: Id, runnable: z.boolean().describe('False: an error finding stops each run before it starts'), findings: z.array(RoutineFinding) }),
  summary: 'The preflight of a routine (CO-1400): the tools of its agent token, its Hermes profile and key, and its Forgejo bot account',
});

export const createRoutine = defineAction({
  name: 'agent.routine.create',
  method: 'post',
  path: '/agent/routines',
  permission: P.keys.update,
  input: z
    .object({
      name: z.string().trim().min(1).max(120),
      ...routineFields,
      domain: routineFields.domain.default(null),
      agentId: routineFields.agentId.default(null),
      cron: routineFields.cron.default(null),
      timezone: routineFields.timezone.default('Europe/Amsterdam'),
      intervalMinutes: routineFields.intervalMinutes.default(null),
      runAt: routineFields.runAt.default(null),
      eventType: routineFields.eventType.default(null),
      eventFilter: routineFields.eventFilter.default({}),
      agentEvents: routineFields.agentEvents.default(false),
      maxRunsPerHour: routineFields.maxRunsPerHour.default(10),
      toolAllowlist: routineFields.toolAllowlist.default(null),
      gate: routineFields.gate.default([]),
      target: routineFields.target.default('none'),
      targetUserIds: routineFields.targetUserIds.default([]),
      deliverAs: routineFields.deliverAs.default('result'),
      chat: routineFields.chat.default(false),
      mailTo: routineFields.mailTo.default([]),
      catchUp: routineFields.catchUp.default('once'),
      repository: routineFields.repository.default(null),
      branch: routineFields.branch.default(null),
      maxTurns: routineFields.maxTurns.default(null),
      timeoutMinutes: routineFields.timeoutMinutes.default(null),
      precheck: routineFields.precheck.default(null),
      pauseAfterFailures: routineFields.pauseAfterFailures.default(5),
    })
    .strict(),
  output: SavedRoutine,
  summary: 'Makes a routine: an instruction for an agent with a trigger (cron with time zone, interval, once or an event) (CO-1250)',
  description:
    'cron needs cron; interval needs intervalMinutes; once needs runAt; event needs eventType (see agent.routine.events). Without agentId the default agent of the domain runs it. ' +
    'target notify tells targetUserIds (or the maker) the result in the app and by mail; with deliverAs brief at once by mail and Hermes chat, as a brief (CO-1392); with chat also through Hermes chat, and chat only with target notify (else routine-chat-target, #1347); target mail_draft puts it as a draft to mailTo in the mailbox of the maker, who sends it.',
});

export const updateRoutine = defineAction({
  name: 'agent.routine.update',
  method: 'patch',
  path: '/agent/routines/{id}',
  permission: P.keys.update,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      expectedVersion: z.number().int().positive().optional(),
      name: z.string().trim().min(1).max(120).optional(),
      instruction: routineFields.instruction.optional(),
      agentId: routineFields.agentId.optional(),
      domain: routineFields.domain.optional(),
      trigger: routineFields.trigger.optional(),
      cron: routineFields.cron.optional(),
      timezone: routineFields.timezone.optional(),
      intervalMinutes: routineFields.intervalMinutes.optional(),
      runAt: routineFields.runAt.optional(),
      eventType: routineFields.eventType.optional(),
      eventFilter: routineFields.eventFilter.optional(),
      agentEvents: routineFields.agentEvents.optional(),
      maxRunsPerHour: routineFields.maxRunsPerHour.optional(),
      toolAllowlist: routineFields.toolAllowlist.optional(),
      gate: routineFields.gate.optional(),
      target: routineFields.target.optional(),
      targetUserIds: routineFields.targetUserIds.optional(),
      deliverAs: routineFields.deliverAs.optional(),
      chat: routineFields.chat.optional(),
      mailTo: routineFields.mailTo.optional(),
      catchUp: routineFields.catchUp.optional(),
      repository: routineFields.repository.optional(),
      branch: routineFields.branch.optional(),
      maxTurns: routineFields.maxTurns.optional(),
      timeoutMinutes: routineFields.timeoutMinutes.optional(),
      precheck: routineFields.precheck.optional(),
      pauseAfterFailures: routineFields.pauseAfterFailures.optional(),
    })
    .strict(),
  output: SavedRoutine,
  summary: 'Changes a routine; a new trigger computes the next fire again',
});

export const pauseRoutine = defineAction({
  name: 'agent.routine.pause',
  method: 'post',
  path: '/agent/routines/{id}/pause',
  permission: P.keys.update,
  successStatus: 200,
  input: z.object({ id: Id }).strict(),
  output: Routine,
  summary: 'Pauses a routine: it does not fire until it is resumed',
});

export const resumeRoutine = defineAction({
  name: 'agent.routine.resume',
  method: 'post',
  path: '/agent/routines/{id}/resume',
  permission: P.keys.update,
  successStatus: 200,
  input: z.object({ id: Id }).strict(),
  output: Routine,
  summary: 'Resumes a routine from now on; missed fires are not made up',
});

export const runRoutine = defineAction({
  name: 'agent.routine.run',
  method: 'post',
  path: '/agent/routines/{id}/run',
  permission: P.keys.update,
  successStatus: 202,
  input: z
    .object({
      id: Id,
      input: z.string().trim().min(1).max(10_000).optional().describe('An instruction for this one run, after that of the routine, such as a message that came through Hermes chat (CO-1249)'),
    })
    .strict(),
  output: z.object({ runId: Id, workflowId: z.string() }),
  summary: 'Runs a routine now, besides its trigger; the run shows under agent.run.get',
});

export const steerRun = defineAction({
  name: 'agent.run.steer',
  method: 'post',
  path: '/agent/runs/{id}/steer',
  permission: P.keys.update,
  successStatus: 202,
  input: z.object({ id: Id, message: z.string().trim().min(1).max(4000).describe('What the agent should do differently or next') }).strict(),
  output: z.object({ runId: Id.describe('The follow-up run'), workflowId: z.string() }),
  summary: 'Steer the work of an agent (CO-1270): stops the run when it still runs, and starts a follow-up in the same session with this instruction',
  description: 'Only for runs of a routine or on an issue. Only a person steers; the follow-up shows under agent.run.get.',
});

export const listRoutineEvents = defineAction({
  name: 'agent.routine.events',
  method: 'get',
  path: '/agent/routine-events',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(z.object({ type: z.string(), fields: z.array(z.string()) })) }),
  summary: 'The events a routine can start on, with the payload fields a filter can use (CO-0730)',
});

// ---------------------------------------------------------------- work claims (U8: CO-1235, CO-1232, CO-0430, CO-1244)

export const WORK_ITEM_TYPES = ['issue', 'task', 'ticket'] as const;
export const CLAIM_STATUSES = ['active', 'in_review', 'done', 'released', 'expired', 'rejected'] as const;
export const OPEN_CLAIM_STATUSES = ['active', 'in_review'] as const;
export const EVIDENCE_KINDS = ['pull', 'commit', 'diff', 'test', 'file', 'link', 'note'] as const;
export const REVIEW_DECISIONS = ['approve', 'changes', 'reject'] as const;
export const CLAIM_CHANGES = ['claimed', 'heartbeat', 'evidence', 'submitted', 'approved', 'changes', 'rejected', 'released', 'expired'] as const;
export const CI_STATES = ['success', 'failure', 'pending', 'error'] as const;

const Evidence = z.object({
  kind: z.enum(EVIDENCE_KINDS),
  ref: z.string().nullable().describe('owner/repo#12 for a pull request, owner/repo@sha for a commit, a file id or a URL'),
  title: z.string(),
  summary: z.string(),
  status: z
    .object({ state: z.string().nullable(), merged: z.boolean().nullable(), ci: z.enum(CI_STATES).nullable(), url: z.string().nullable() })
    .nullable()
    .describe('Of a pull request or commit: its state, merge and CI when it was added (CO-0430)'),
  at: IsoDateTime,
  byName: z.string(),
});

export const WorkClaim = z
  .object({
    id: Id,
    itemType: z.enum(WORK_ITEM_TYPES),
    itemId: Id,
    item: z
      .object({
        key: z.string().describe('owner/repo#12, T-12 or S-00012'),
        title: z.string(),
        url: z.string(),
        status: z.string().nullable(),
        projectId: Id.nullable(),
        organisationId: Id.nullable(),
        engagementId: Id.nullable(),
        repository: z.string().nullable().describe('The repository of an issue, or of the project'),
      })
      .nullable()
      .describe('The work item, when the caller may still read it'),
    principalId: Id,
    holderName: z.string(),
    holderKind: z.enum(['human', 'agent']),
    agentId: Id.nullable(),
    status: z.enum(CLAIM_STATUSES),
    leaseUntil: IsoDateTime.nullable().describe('An active claim expires after this, unless a heartbeat extends it'),
    acceptanceCriteria: z.string(),
    expectedResult: z.string(),
    evidence: z.array(Evidence),
    review: z.object({ decision: z.enum(REVIEW_DECISIONS), comment: z.string(), byName: z.string(), at: IsoDateTime }).nullable(),
    runId: Id.nullable(),
    claimedAt: IsoDateTime,
    closedAt: IsoDateTime.nullable(),
    version: z.number().int(),
  })
  .openapi('WorkClaim');

const ClaimId = z.object({ id: Id });
const Markdown = z.string().max(20_000);

export const claimTask = defineAction({
  name: 'agent.task.claim',
  method: 'post',
  path: '/agent/tasks',
  permission: P.keys.read,
  input: z
    .object({
      itemType: z.enum(WORK_ITEM_TYPES),
      itemId: Id,
      acceptanceCriteria: Markdown.optional().describe('When the work is done; default from the work item'),
      expectedResult: Markdown.optional().describe('What it delivers, such as a pull request with tests'),
      runId: Id.optional().describe('The run that works on it; default the run of the caller'),
      leaseMinutes: z.number().int().min(5).max(240).optional().describe('Default 30; a heartbeat extends it'),
    })
    .strict(),
  output: WorkClaim,
  summary: 'Claim an issue, task or ticket so no one else picks it up; 409 claim-taken when someone else has it (CO-1235)',
});

export const listTasks = defineAction({
  name: 'agent.task.list',
  method: 'get',
  path: '/agent/tasks',
  permission: P.keys.read,
  input: ListQuery.extend({
    mine: z.coerce.boolean().optional().describe('Only the claims of the caller'),
    agentId: Id.optional(),
    status: z
      .string()
      .regex(/^[a-z_]+(,[a-z_]+)*$/)
      .optional()
      .describe('Comma separated; default active and in_review'),
    itemType: z.enum(WORK_ITEM_TYPES).optional(),
    itemId: Id.optional(),
  }),
  output: Paginated(WorkClaim),
  summary: 'Claims on issues, tasks and tickets: your own, of an agent, or on one work item, newest first (CO-1235)',
});

const CsvIds = z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((x) => x.trim()).filter(Boolean) : v), z.array(Id).min(1).max(200));

export const ItemAgentStatus = z
  .object({
    itemId: Id,
    claimId: Id,
    status: z.enum(CLAIM_STATUSES),
    agentId: Id.nullable(),
    agentName: z.string().nullable(),
    runId: Id.nullable().describe('Its run that runs now, if any'),
    pullRef: z.string().nullable().describe('owner/repo#12 of the pull request in its evidence'),
    pullUrl: z.string().nullable(),
  })
  .openapi('ItemAgentStatus');

export const taskStatus = defineAction({
  name: 'agent.task.status',
  method: 'get',
  path: '/agent/tasks/status',
  permission: P.keys.read,
  input: z.object({ itemType: z.enum(WORK_ITEM_TYPES), itemIds: CsvIds.describe('Up to 200 ids, comma separated') }),
  output: z.object({ items: z.array(ItemAgentStatus) }),
  summary: 'Who works on these issues, tasks or tickets: the open claim, its running run and its pull request, for cards and boards (CO-1245)',
});

export const getTask = defineAction({
  name: 'agent.task.get',
  method: 'get',
  path: '/agent/tasks/{id}',
  permission: P.keys.read,
  input: ClaimId,
  output: WorkClaim,
  summary: 'One claim with its work item, acceptance criteria, evidence and review (CO-1235, CO-1248)',
});

export const heartbeatTask = defineAction({
  name: 'agent.task.heartbeat',
  method: 'post',
  path: '/agent/tasks/{id}/heartbeat',
  permission: P.keys.read,
  input: ClaimId.extend({ leaseMinutes: z.number().int().min(5).max(240).optional() }).strict(),
  output: WorkClaim,
  successStatus: 200,
  summary: 'Extend the lease of your active claim; without it the claim expires and the work item is free again (CO-1244)',
});

export const addEvidence = defineAction({
  name: 'agent.task.evidence',
  method: 'post',
  path: '/agent/tasks/{id}/evidence',
  permission: P.keys.read,
  input: ClaimId.extend({
    kind: z.enum(EVIDENCE_KINDS),
    ref: z.string().trim().max(500).optional(),
    title: z.string().trim().min(1).max(300),
    summary: z.string().max(10_000).optional().describe('Test output, the diff in short, what the file shows'),
  }).strict(),
  output: WorkClaim,
  successStatus: 200,
  summary: 'Add evidence to your claim: a pull request, commit, diff, test output, file or link (CO-1232, CO-0430)',
});

export const submitTask = defineAction({
  name: 'agent.task.submit',
  method: 'post',
  path: '/agent/tasks/{id}/submit',
  permission: P.keys.read,
  input: ClaimId.extend({ note: z.string().max(4000).optional() }).strict(),
  output: WorkClaim,
  successStatus: 200,
  summary: 'Hand in your claim for review; a pull request in the evidence needs a CI that is not red (CO-1232)',
});

export const reviewTask = defineAction({
  name: 'agent.task.review',
  method: 'post',
  path: '/agent/tasks/{id}/review',
  permission: P.keys.update,
  input: ClaimId.extend({ decision: z.enum(REVIEW_DECISIONS), comment: z.string().max(4000).optional() }).strict(),
  output: WorkClaim,
  successStatus: 200,
  summary: 'Review a claim in review: approve (done), changes (back to the agent) or reject (released); never by an agent (CO-1232)',
});

export const releaseTask = defineAction({
  name: 'agent.task.release',
  method: 'post',
  path: '/agent/tasks/{id}/release',
  permission: P.keys.read,
  input: ClaimId.extend({ reason: z.string().max(1000).optional() }).strict(),
  output: WorkClaim,
  successStatus: 200,
  summary: 'Give a claim back so the work item is free; your own, or any with agents:manage (CO-1235)',
});

/** A claim changed (CO-1235): the wake queue and the boards follow it. */
export const taskChanged = defineEvent({
  type: 'agent.task.changed',
  version: 1,
  payload: z.object({
    claimId: Id,
    itemType: z.enum(WORK_ITEM_TYPES),
    itemId: Id,
    principalId: Id,
    agentId: Id.nullable(),
    change: z.enum(CLAIM_CHANGES),
  }),
});

// ---------------------------------------------------------------- wakes, threads and messages (U8: CO-1240, CO-1242, CO-1252, CO-0934, CO-0938)

export const WAKE_REASONS = ['interval', 'assignment', 'comment', 'message', 'approval', 'scheduled'] as const;
export const WAKE_STATUSES = ['pending', 'consumed', 'cancelled'] as const;
/** chat: the lasting chat of one person with an agent in the web app (CO-0857); its subject is the person. */
export const SUBJECT_TYPES = ['issue', 'task', 'ticket', 'claim', 'thread', 'routine', 'chat'] as const;
export const THREAD_STATUSES = ['open', 'closed'] as const;
export const MESSAGE_KINDS = ['progress', 'instruction', 'question', 'answer', 'plan', 'note'] as const;

export const AgentWake = z
  .object({
    id: Id,
    agentId: Id,
    reason: z.enum(WAKE_REASONS),
    subjectType: z.enum(SUBJECT_TYPES).nullable(),
    subjectId: Id.nullable(),
    note: z.string(),
    dueAt: IsoDateTime,
    status: z.enum(WAKE_STATUSES),
    runId: Id.nullable().describe('The run that took it up'),
    createdAt: IsoDateTime,
  })
  .openapi('AgentWake');

const WorkState = z
  .object({
    phase: z.string().max(200).optional(),
    step: z.string().max(500).optional(),
    blockers: z.array(z.string().max(500)).max(20).optional(),
    nextAction: z.string().max(1000).optional(),
    notes: z.string().max(5000).optional(),
  })
  .strict();

export const AgentThread = z
  .object({
    id: Id,
    agentId: Id,
    agentName: z.string(),
    title: z.string(),
    subjectType: z.enum(SUBJECT_TYPES).nullable(),
    subjectId: Id.nullable(),
    status: z.enum(THREAD_STATUSES),
    workState: WorkState.describe('Where the work stands, kept by the agent'),
    sessionKey: z.string(),
    activeRunId: Id.nullable(),
    updatedAt: IsoDateTime,
    version: z.number().int(),
  })
  .openapi('AgentThread');

export const AgentMessage = z
  .object({
    id: Id,
    threadId: Id,
    kind: z.enum(MESSAGE_KINDS),
    body: z.string(),
    authorName: z.string(),
    authorKind: z.enum(['human', 'agent', 'system']),
    runId: Id.nullable(),
    needsAnswer: z.boolean(),
    approvalId: Id.nullable().describe('A question or plan waits in the approval inbox'),
    planVersion: z.number().int().nullable(),
    answeredAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
  })
  .openapi('AgentMessage');

export const scheduleWake = defineAction({
  name: 'agent.wake.schedule',
  method: 'post',
  path: '/agent/wakes',
  permission: P.keys.read,
  input: z
    .object({
      agentId: Id.optional().describe('Default: the calling agent itself'),
      dueAt: IsoDateTime.describe('When to wake'),
      subjectType: z.enum(SUBJECT_TYPES).optional(),
      subjectId: Id.optional(),
      note: z.string().trim().max(2000).optional().describe('What to do then, for the run that takes it up'),
    })
    .strict(),
  output: AgentWake,
  summary: 'Set a wake for yourself (or, as a person, for an agent): at that moment a run picks it up (CO-1240)',
});

export const listWakes = defineAction({
  name: 'agent.wake.list',
  method: 'get',
  path: '/agent/wakes',
  permission: P.keys.read,
  input: ListQuery.extend({ agentId: Id.optional(), status: z.enum(WAKE_STATUSES).optional().describe('Default pending') }),
  output: Paginated(AgentWake),
  summary: 'The wakes of an agent: pending ones by default, the earliest first (CO-1240)',
});

export const listThreads = defineAction({
  name: 'agent.thread.list',
  method: 'get',
  path: '/agent/threads',
  permission: P.keys.read,
  input: ListQuery.extend({
    agentId: Id.optional(),
    mine: z.coerce.boolean().optional().describe('The threads of the calling agent'),
    status: z.enum(THREAD_STATUSES).optional().describe('Default open'),
    subjectType: z.enum(SUBJECT_TYPES).optional(),
    subjectId: Id.optional(),
  }),
  output: Paginated(AgentThread),
  summary: 'The threads of agents: the ongoing work per subject with its work state, last changed first (CO-1242)',
});

export const getThread = defineAction({
  name: 'agent.thread.get',
  method: 'get',
  path: '/agent/threads/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: AgentThread.extend({ messages: z.array(AgentMessage) }),
  summary: 'One thread with its messages, oldest first (CO-1242)',
});

export const updateThread = defineAction({
  name: 'agent.thread.update',
  method: 'patch',
  path: '/agent/threads/{id}',
  permission: P.keys.read,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      title: z.string().trim().min(1).max(300).optional(),
      workState: WorkState.optional().describe('Replaces the work state'),
      status: z.enum(THREAD_STATUSES).optional(),
    })
    .strict(),
  output: AgentThread,
  summary: 'Keep the work state of your thread (phase, step, blockers, next action), or close it (CO-1242)',
});

export const sendMessage = defineAction({
  name: 'agent.message.send',
  method: 'post',
  path: '/agent/messages',
  permission: P.keys.read,
  input: z
    .object({
      threadId: Id.optional(),
      agentId: Id.optional().describe('A new thread of this agent, when threadId is not given'),
      subjectType: z.enum(SUBJECT_TYPES).optional(),
      subjectId: Id.optional(),
      title: z.string().trim().max(300).optional(),
      kind: z.enum(MESSAGE_KINDS),
      body: z.string().trim().min(1).max(20_000),
      needsAnswer: z.boolean().optional().describe('A question that waits for a person (CO-0934)'),
    })
    .strict(),
  output: AgentMessage,
  summary: 'Post on a thread: progress, a question for a person, a plan to approve (agent), or an instruction (person) that wakes the agent (CO-1242, CO-0934, CO-0938)',
});

export const listMessages = defineAction({
  name: 'agent.message.list',
  method: 'get',
  path: '/agent/messages',
  permission: P.keys.read,
  input: ListQuery.extend({
    threadId: Id.optional(),
    mine: z.coerce.boolean().optional().describe('The messages on the threads of the calling agent'),
    since: IsoDateTime.optional(),
    unanswered: z.coerce.boolean().optional().describe('Only questions without an answer yet'),
  }),
  output: Paginated(AgentMessage),
  summary: 'The messages of a thread or of your own threads, oldest first; read new instructions between your steps (CO-1242, CO-1252)',
});

export const startSession = defineAction({
  name: 'agent.session.start',
  method: 'post',
  path: '/agent/agents/{id}/sessions',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      instruction: z.string().trim().min(1).max(20_000),
      title: z.string().trim().max(300).optional(),
      subjectType: z.enum(SUBJECT_TYPES).optional(),
      subjectId: Id.optional(),
    })
    .strict(),
  output: AgentThread,
  successStatus: 202,
  summary: 'Start a session of an agent: a thread with the instruction, and a wake so a run picks it up now (CO-1252)',
});

export const cancelRun = defineAction({
  name: 'agent.session.cancel',
  method: 'post',
  path: '/agent/runs/{id}/cancel',
  permission: P.keys.update,
  input: z.object({ id: Id, reason: z.string().trim().max(500).optional() }).strict(),
  output: z.object({ runId: Id, status: z.string() }),
  successStatus: 202,
  summary: 'Stop a running agent run: the runtime stops it at its next look, without a follow-up (CO-1252)',
});

export const openChat = defineAction({
  name: 'agent.chat.open',
  method: 'post',
  path: '/agent/chats',
  permission: P.keys.chat,
  successStatus: 200,
  input: z.object({ agentId: Id }).strict(),
  output: AgentThread.extend({ messages: z.array(AgentMessage) }),
  summary: 'Your lasting chat with an agent: one open thread per person and agent, made when it does not exist yet; post on it with agent.message.send (CO-0857)',
});

/** A message on a thread (CO-1242): an instruction wakes the agent, a question or plan goes to the inbox. */
export const messageSent = defineEvent({
  type: 'agent.message.sent',
  version: 1,
  payload: z.object({ messageId: Id, threadId: Id, agentId: Id, kind: z.enum(MESSAGE_KINDS), authorKind: z.enum(['human', 'agent', 'system']) }),
});

// ---------------------------------------------------------------- budgets per agent and engagement (U8: CO-0820, CO-1237)

export const BUDGET_SCOPES = ['agent', 'engagement'] as const;

export const AgentBudget = z
  .object({
    id: Id,
    scope: z.enum(BUDGET_SCOPES),
    scopeId: Id,
    name: z.string().nullable().describe('The name of the agent; an engagement by its id'),
    monthlyMicros: z.number().int().nullable(),
    currency: z.string(),
    month: z.string(),
    spentMicros: z.number().int(),
    projectedMicros: z.number().int().describe('At the end of the month at this pace'),
    percent: z.number().int().nullable(),
    stoppedAt: IsoDateTime.nullable().describe('At 100% this month: the agent is paused, runs on the engagement wait'),
    resumeOnce: z.boolean().describe('A person let it go on for one more run'),
    approvalId: Id.nullable().describe('The request in the inbox while it is stopped'),
    version: z.number().int(),
  })
  .openapi('AgentBudget');

export const listBudgets = defineAction({
  name: 'agent.budget.list',
  method: 'get',
  path: '/agent/budgets',
  permission: P.keys.read,
  input: z.object({ scope: z.enum(BUDGET_SCOPES).optional(), scopeId: Id.optional() }),
  output: z.object({ items: z.array(AgentBudget) }),
  summary: 'The monthly caps of agents and engagements with their spend, pace and stop (CO-0820)',
});

export const capBudget = defineAction({
  name: 'agent.budget.cap',
  method: 'put',
  path: '/agent/budgets',
  permission: P.keys.manage,
  input: z
    .object({
      scope: z.enum(BUDGET_SCOPES),
      scopeId: Id,
      monthlyMicros: z.number().int().positive().nullable().describe('Null removes the cap'),
      expectedVersion: z.number().int().positive().optional(),
    })
    .strict(),
  output: AgentBudget,
  successStatus: 200,
  summary: 'Set the monthly cap of an agent or engagement; a cap above the spend lifts a stop and the agent goes on (CO-0820, CO-1237)',
});

// ---------------------------------------------------------------- revisions, export and import (U8: CO-1258)

export const AgentRevision = z
  .object({
    number: z.number().int(),
    authorName: z.string().nullable(),
    at: IsoDateTime,
    note: z.string(),
    changes: z.record(z.string(), z.object({ before: z.unknown(), after: z.unknown() })).describe('What changed against the revision before'),
    config: AgentConfig,
  })
  .openapi('AgentRevision');

export const listRevisions = defineAction({
  name: 'agent.revision.list',
  method: 'get',
  path: '/agent/agents/{id}/revisions',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: z.object({ items: z.array(AgentRevision) }),
  summary: 'The revisions of the configuration of an agent, newest first, with what changed (CO-1258)',
});

export const revertAgent = defineAction({
  name: 'agent.agent.revert',
  method: 'post',
  path: '/agent/agents/{id}/revert',
  permission: P.keys.update,
  successStatus: 200,
  input: z.object({ id: Id, number: z.number().int().min(1), expectedVersion: z.number().int().positive().optional() }).strict(),
  output: Agent,
  summary: 'Puts the configuration of an earlier revision back, as a new revision; it holds from the next run (CO-1258)',
});

export const exportAgent = defineAction({
  name: 'agent.agent.export',
  method: 'get',
  path: '/agent/agents/{id}/export',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: z.object({ name: z.string(), yaml: z.string() }),
  summary: 'The configuration of an agent as YAML, to keep in a repository or to import elsewhere (CO-1258)',
});

export const importAgent = defineAction({
  name: 'agent.agent.import',
  method: 'post',
  path: '/agent/agents/import',
  permission: P.keys.create,
  successStatus: 200,
  input: z
    .object({
      yaml: z.string().min(1).max(100_000).describe('As agent.agent.export gives it'),
      roleKeys: z.array(z.string()).min(1).max(10).default(['agent']).describe('Roles of a new agent; an existing one keeps its roles'),
    })
    .strict(),
  output: Agent,
  summary: 'Imports an agent from YAML: an agent with that name gets the configuration as a new revision, else it is created (CO-1258)',
});

// ---------------------------------------------------------------- in-app agents (U8: CO-0834, CO-0835, CO-0839, CO-0833)

export const InAppDefinitionView = z
  .object({
    name: z.string(),
    title: z.string(),
    description: z.string(),
    instructions: z.string().describe('Markdown, from the code of the app'),
    modelAlias: z.enum(MODEL_ALIASES),
    tools: z.array(z.object({ name: z.string(), fixed: z.boolean().describe('Some parameters come from the input of the run') })),
    limits: z.object({ maxSteps: z.number().int(), maxCalls: z.number().int().nullable(), maxTokens: z.number().int().nullable(), maxDurationMs: z.number().int().nullable(), maxToolCalls: z.number().int().nullable() }),
    output: z.record(z.string(), z.unknown()).describe('The JSON schema of the final answer'),
  })
  .openapi('InAppDefinition');

export const listDefinitions = defineAction({
  name: 'agent.definition.list',
  method: 'get',
  path: '/agent/definitions',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(InAppDefinitionView) }),
  summary: 'The in-app agents defined in the code of the app: instructions, model, tools, output and limits (CO-0834)',
});

// ---------------------------------------------------------------- workers and sessions (U8: CO-1243, CO-1256, CO-0136, CO-0038, CO-1239)

export const WORKER_RUNTIMES = ['claude_code', 'cli'] as const;
export const WORKER_STATUSES = ['online', 'offline'] as const;
export const SESSION_END_STATUSES = ['succeeded', 'failed', 'cancelled'] as const;

export const AgentWorker = z
  .object({
    id: Id,
    name: z.string(),
    host: z.string(),
    runtimes: z.array(z.enum(WORKER_RUNTIMES)),
    version: z.string(),
    capacity: z.number().int().describe('Sessions at the same time'),
    labels: z.array(z.string()).describe('It runs only agents with one of these labels; empty: only agents without a label (CO-1367)'),
    status: z.enum(WORKER_STATUSES),
    lastHeartbeatAt: IsoDateTime.nullable(),
    sessions: z.array(z.object({ runId: Id, agentId: Id.nullable(), agentName: z.string().nullable(), startedAt: IsoDateTime })).describe('The sessions it runs now'),
    lastError: z.string().nullable(),
    lastErrorAt: IsoDateTime.nullable(),
    identityName: z.string().describe('The worker identity whose token it uses'),
    createdAt: IsoDateTime,
  })
  .openapi('AgentWorker');

export const WorkerSession = z
  .object({
    runId: Id,
    agentId: Id,
    agentName: z.string(),
    runtime: z.enum(WORKER_RUNTIMES),
    threadId: Id.nullable(),
    sessionKey: z.string().describe('The same key for every run on one thread: a worker may resume the session it had'),
    prompt: z.string().describe('The task: why the agent was woken, its open claims and how to work'),
    instructions: z.string().describe('The persona and rules of the agent, for the system prompt'),
    repositories: z.array(z.object({ fullName: z.string(), cloneUrl: z.string().nullable(), branch: z.string().nullable() })).describe('The repositories of the work items or the routine of this run'),
    maxTurns: z.number().int().nullable().describe('At most this many turns (a routine, CO-1361)'),
    token: z.string().describe('The run token for MKCore (MKCORE_TOKEN): valid only while this run runs; never log it'),
    mcpUrl: z.string(),
    forgejoLogin: z.string().nullable().describe('The bot account of the agent in Forgejo (CO-1239)'),
    forgejoToken: z.string().nullable().describe('Its token, for git push to a branch; null without a bot account; never log it'),
    maxDurationMs: z.number().int(),
    startedAt: IsoDateTime,
  })
  .openapi('WorkerSession');

const WorkerId = z.object({ id: Id });
const SessionEvent = z
  .object({
    kind: z.enum(RUN_EVENT_KINDS),
    name: z.string().max(200).optional().describe('The tool or the kind of message'),
    data: z.unknown().optional().describe('What happened; secrets are masked before it is stored'),
  })
  .strict();

export const registerWorker = defineAction({
  name: 'agent.worker.register',
  method: 'post',
  path: '/agent/workers',
  permission: P.keys.work,
  successStatus: 200,
  input: z
    .object({
      name: z.string().trim().min(1).max(120).describe('Unique per company; registering again under the same name is the same worker after a restart'),
      host: z.string().trim().max(200).default(''),
      runtimes: z.array(z.enum(WORKER_RUNTIMES)).min(1),
      version: z.string().trim().max(40).default(''),
      capacity: z.number().int().min(1).max(20).default(1),
      labels: z
        .array(Label)
        .max(10)
        .default([])
        .describe('Agents with one of these labels run here, and only here; empty: only agents without a label (CO-1367)'),
    })
    .strict(),
  output: AgentWorker,
  // For the worker daemon, never for a model: its answers carry tokens.
  restOnly: true,
  summary: 'A worker daemon registers: its runtimes and capacity; sessions it ran before a restart go back to the queue (CO-1256)',
});

export const workerHeartbeat = defineAction({
  name: 'agent.worker.heartbeat',
  method: 'post',
  path: '/agent/workers/{id}/heartbeat',
  permission: P.keys.work,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      sessions: z.array(Id).max(50).describe('The runs it runs now'),
      error: z.string().trim().max(2000).nullable().optional().describe('The last error of the worker itself, or null when it is fine again'),
    })
    .strict(),
  output: z.object({
    cancel: z.array(Id).describe('Sessions to stop now: a person cancelled them, they ran past their time, or the run ended'),
    lost: z.array(Id).describe('Runs the app gave this worker that it does not run: they went back to the queue'),
  }),
  restOnly: true,
  summary: 'Every 30 seconds: the worker is online and runs these sessions; the answer says which to stop (CO-1256)',
});

export const nextSession = defineAction({
  name: 'agent.worker.next',
  method: 'post',
  path: '/agent/workers/{id}/next',
  permission: P.keys.work,
  successStatus: 200,
  input: WorkerId.extend({
    waitSeconds: z
      .number()
      .int()
      .min(0)
      .max(25)
      .default(0)
      .describe('Long poll (CO-1398): with nothing to do, wait up to this many seconds for work before answering null; 0 answers at once'),
  }).strict(),
  output: z.object({ session: WorkerSession.nullable().describe('Null when nothing waits for a runtime of this worker') }),
  restOnly: true,
  secret: true,
  summary: 'Fetch the next session for a runtime of this worker: the run, the agent, the task, the repositories and a run token (CO-0136, CO-0038)',
  description: 'With waitSeconds the call waits up to 25 seconds for work and answers as soon as a session is there (CO-1398); the call also counts as a heartbeat of the worker.',
});

export const sessionEvents = defineAction({
  name: 'agent.session.event',
  method: 'post',
  path: '/agent/sessions/{runId}/events',
  permission: P.keys.read,
  successStatus: 200,
  input: z.object({ runId: Id, events: z.array(SessionEvent).min(1).max(100) }).strict(),
  output: z.object({ accepted: z.number().int(), cancel: z.boolean().describe('Stop the session: a person cancelled it, or the run ended') }),
  summary: 'Report what a session does (messages, tool calls, errors) on its run, with the run token or as its worker (CO-0136)',
});

export const finishSession = defineAction({
  name: 'agent.session.finish',
  method: 'post',
  path: '/agent/sessions/{runId}/finish',
  permission: P.keys.read,
  successStatus: 200,
  input: z
    .object({
      runId: Id,
      status: z.enum(SESSION_END_STATUSES),
      result: z.string().max(RUN_OUTCOME_MAX_CHARS).optional().describe('The final answer of the session, whole; the run and its answer event keep it as it is'),
      error: z.string().max(4000).optional(),
      usage: z
        .object({
          model: z.string().max(120).optional(),
          inputTokens: z.number().int().min(0),
          outputTokens: z.number().int().min(0),
          costMicros: z.number().int().min(0).optional().describe('What the runtime reports; else the app prices the tokens (AI_PRICES)'),
        })
        .strict()
        .optional(),
    })
    .strict(),
  output: z.object({ runId: Id, status: z.enum(RUN_STATUSES), costMicros: z.number().int() }),
  summary: 'End a session: status, result and usage on its run; its run token stops working (CO-0136, CO-0038)',
});

export const listWorkers = defineAction({
  name: 'agent.worker.list',
  method: 'get',
  path: '/agent/workers',
  permission: P.keys.read,
  input: z.object({ status: z.enum(WORKER_STATUSES).optional() }),
  output: z.object({ items: z.array(AgentWorker) }),
  summary: 'The workers with status, last heartbeat, running sessions and their last error (CO-1256)',
});

export const setForgejoToken = defineAction({
  name: 'agent.agent.setForgejoToken',
  method: 'put',
  path: '/agent/agents/{id}/forgejo',
  permission: P.keys.manage,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      login: z.string().trim().min(1).max(100).describe('The login of the bot account in Forgejo'),
      token: z.string().trim().min(8).max(500).describe('An access token of that account; never shown again'),
    })
    .strict(),
  output: z.object({ provider: z.string(), accountLogin: z.string().nullable(), status: z.string() }),
  // A person types the token in the web app; it never passes through a model.
  restOnly: true,
  summary: 'Set the Forgejo bot account of an agent: write-through and pull requests of the agent run under it (CO-1239)',
});

export const shareForgejoBot = defineAction({
  name: 'agent.agent.shareForgejoBot',
  method: 'put',
  path: '/agent/agents/{id}/forgejo/share',
  permission: P.keys.manage,
  successStatus: 200,
  input: z
    .object({
      id: Id.describe('The agent that gets the bot account'),
      fromAgentId: Id.describe('The agent whose bot account is reused'),
    })
    .strict(),
  output: z.object({ provider: z.string(), accountLogin: z.string().nullable(), status: z.string() }),
  summary: 'Reuse the Forgejo bot account of one agent for another; the server copies it, the token is never in the call or the result (CO-1239)',
});

// ---------------------------------------------------------------- cockpit and timeline (U8: CO-1241, CO-0980, CO-1254, CO-1259)

const Count = z.number().int();

export const CockpitAgent = z
  .object({
    id: Id,
    principalId: Id,
    name: z.string(),
    role: z.string(),
    runtime: z.enum(AGENT_RUNTIMES),
    status: z.enum(AGENT_STATUSES),
    statusReason: z.string().nullable(),
    autonomy: z.enum(AUTONOMY_LEVELS),
    managerId: Id.nullable(),
    running: z.array(z.object({ runId: Id, task: z.string(), startedAt: IsoDateTime, workerName: z.string().nullable(), threadId: Id.nullable() })).describe('Its runs that run now'),
    workState: z
      .object({ threadId: Id, title: z.string(), phase: z.string().nullable(), step: z.string().nullable(), blockers: z.array(z.string()), nextAction: z.string().nullable() })
      .nullable()
      .describe('Where the work stands on its most recent open thread'),
    claims: z.object({ active: Count, inReview: Count }),
    openQuestions: Count.describe('Questions on its threads that wait for a person'),
    pendingApprovals: Count.describe('Its calls and drafts that wait in the approval inbox'),
    spentMicros: Count.describe('AI costs this month'),
    capMicros: Count.nullable().describe('Its monthly cap, if any'),
    lastRun: z.object({ id: Id, status: z.enum(RUN_STATUSES), startedAt: IsoDateTime, finishedAt: IsoDateTime.nullable() }).nullable(),
  })
  .openapi('CockpitAgent');

export const Cockpit = z
  .object({
    agents: z.array(CockpitAgent),
    people: z.array(z.object({ id: Id, name: z.string() })).describe('The people agents report to, for the org chart'),
    workers: z.array(z.object({ id: Id, name: z.string(), status: z.enum(WORKER_STATUSES), sessions: Count, capacity: Count })),
    totals: z.object({ active: Count, running: Count, openQuestions: Count, pendingApprovals: Count, spentMicros: Count, currency: z.string() }),
  })
  .openapi('Cockpit');

export const getCockpit = defineAction({
  name: 'agent.cockpit.get',
  method: 'get',
  path: '/agent/cockpit',
  permission: P.keys.read,
  input: z.object({ terminated: z.coerce.boolean().optional().describe('Also terminated agents') }),
  output: Cockpit,
  summary: 'The operator cockpit: per agent its status, running runs, work state, claims, open questions and approvals, spend; the workers and the org chart (CO-1241, CO-1254)',
});

export const TIMELINE_KINDS = ['run', 'claim', 'revision'] as const;

export const AgentTimeline = z
  .object({
    entries: z.array(
      z.object({
        kind: z.enum(TIMELINE_KINDS),
        at: IsoDateTime,
        title: z.string(),
        status: z.string(),
        detail: z.string().nullable(),
        costMicros: Count.nullable(),
        durationMs: Count.nullable(),
        path: z.string().nullable().describe('Where it opens in the web app'),
      }),
    ),
    stats: z.object({
      runs: Count,
      succeeded: Count,
      failed: Count,
      cancelled: Count,
      claimsDone: Count,
      claimsRejected: Count,
      costMicros: Count,
      currency: z.string(),
      avgDurationMs: Count.nullable(),
    }),
  })
  .openapi('AgentTimeline');

export const agentTimeline = defineAction({
  name: 'agent.agent.timeline',
  method: 'get',
  path: '/agent/agents/{id}/timeline',
  permission: P.keys.read,
  input: z.object({ id: Id, days: z.coerce.number().int().min(1).max(365).default(30) }),
  output: AgentTimeline,
  summary: 'The work history of an agent: runs, claims and revisions with outcome, costs and lead time, plus totals (CO-1259)',
});

/** A run started (CO-0980): the cockpit shows it at once. */
export const agentRunStarted = defineEvent({
  type: 'agent.run.started',
  version: 1,
  payload: z.object({ runId: Id, agentId: Id.nullable(), task: z.string() }),
});

/** The work state or status of a thread changed (CO-0980). */
export const threadChanged = defineEvent({
  type: 'agent.thread.changed',
  version: 1,
  payload: z.object({ threadId: Id, agentId: Id }),
});

/** A run ended (CO-1269): time books the agent time of it on its project or issue (ADR-0014). */
export const agentRunFinished = defineEvent({
  type: 'agent.run.finished',
  version: 1,
  payload: z.object({
    runId: Id,
    agentId: Id.nullable(),
    agentPrincipalId: Id.nullable().describe('The identity of the agent, which time entries refer to'),
    status: z.enum(RUN_STATUSES),
    task: z.string(),
    startedAt: IsoDateTime,
    finishedAt: IsoDateTime,
    projectId: Id.nullable(),
    issueId: Id.nullable(),
  }),
});

export const routineChanged = defineEvent({
  type: 'agent.routine.changed',
  version: 1,
  payload: z.object({ routineId: Id, change: z.enum(['created', 'updated', 'paused', 'resumed']) }),
});

export const routineFired = defineEvent({
  type: 'agent.routine.fired',
  version: 1,
  payload: z.object({ routineId: Id, runId: Id, trigger: z.enum(['cron', 'interval', 'once', 'event', 'manual']) }),
});

export const agentChanged = defineEvent({
  type: 'agent.agent.changed',
  version: 1,
  payload: z.object({ agentId: Id, principalId: Id, change: z.enum(['created', 'updated', 'paused', 'resumed', 'terminated']) }),
});

// ---------------------------------------------------------------- external MCP servers (CO-0838, ADR-0034)

export const MCP_TRANSPORTS = ['http', 'stdio'] as const;
export const MCP_AUTHS = ['none', 'bearer', 'oauth'] as const;
export const MCP_TOOL_MODES = ['read', 'write'] as const;
/** The approval kind of a write tool of an external MCP server: the call runs after a person approved it. */
export const MCP_TOOL_KIND = 'agent.mcpTool';
const McpName = z
  .string()
  .regex(/^[a-z][a-z0-9]{1,29}$/, 'Lower case letters and digits, 2 to 30, starting with a letter')
  .describe('Short name, also the prefix of its tools for agents: name/tool');
const SecretName = z
  .string()
  .regex(/^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)+$/, 'The name of a secret, such as mcp.woodpecker.token')
  .describe('The name of a secret in the app (admin.secret.*), never the value');
const ExpectedVersion = z.number().int().positive().optional().describe('Version you read; the update fails if it changed');

export const McpServerTool = z
  .object({
    name: z.string().max(100),
    title: z.string().nullable(),
    description: z.string(),
    inputSchema: z.record(z.string(), z.unknown()),
    readOnlyHint: z.boolean().describe('The server says the tool only reads'),
    enabled: z.boolean().describe('Agents may use it; off until an admin turns it on'),
    mode: z.enum(MCP_TOOL_MODES).describe('read runs directly; write waits for a person (approval kind agent.mcpTool)'),
  })
  .openapi('McpServerTool');

export const McpServer = z
  .object({
    id: Id,
    name: z.string(),
    title: z.string(),
    transport: z.enum(MCP_TRANSPORTS),
    url: z.string().nullable().describe('http: the endpoint of the server'),
    package: z.string().nullable().describe('stdio: the npm package, one of MCP_STDIO_PACKAGES'),
    auth: z.enum(MCP_AUTHS),
    bearerSecret: z.string().nullable().describe('auth bearer: the secret with the token'),
    envSecrets: z.record(z.string(), z.string()).describe('stdio: environment variables of the child and the secret each comes from'),
    oauthScope: z.string().nullable(),
    oauthStatus: z.enum(['none', 'pending', 'linked']).describe('auth oauth: linked when the app has tokens'),
    tools: z.array(McpServerTool),
    timeoutMs: z.number().int(),
    enabled: z.boolean(),
    lastSyncAt: IsoDateTime.nullable().describe('When the tools were last read from the server'),
    lastError: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    version: z.number().int(),
  })
  .openapi('McpServer');

const mcpServerFields = {
  title: z.string().trim().min(1).max(120),
  transport: z.enum(MCP_TRANSPORTS),
  url: z.url({ protocol: /^https?$/ }).max(500).nullable(),
  package: z.string().trim().max(214).nullable(),
  auth: z.enum(MCP_AUTHS),
  bearerSecret: SecretName.nullable(),
  envSecrets: z.record(z.string().regex(/^[A-Z][A-Z0-9_]{0,63}$/, 'An environment variable such as API_TOKEN'), SecretName).refine((r) => Object.keys(r).length <= 20, 'At most 20 variables'),
  oauthScope: z.string().trim().max(500).nullable(),
  timeoutMs: z.number().int().min(1000).max(300_000),
  enabled: z.boolean(),
};

export const listMcpServers = defineAction({
  name: 'agent.mcpServer.list',
  method: 'get',
  path: '/agent/mcp-servers',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(McpServer) }),
  summary: 'The external MCP servers in-app agents may call, with their tools (CO-0838)',
});

export const getMcpServer = defineAction({
  name: 'agent.mcpServer.get',
  method: 'get',
  path: '/agent/mcp-servers/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: McpServer,
  summary: 'One external MCP server with its tools (CO-0838)',
});

export const createMcpServer = defineAction({
  name: 'agent.mcpServer.create',
  method: 'post',
  path: '/agent/mcp-servers',
  permission: P.keys.manage,
  input: z
    .object({
      name: McpName,
      ...mcpServerFields,
      url: mcpServerFields.url.default(null),
      package: mcpServerFields.package.default(null),
      auth: mcpServerFields.auth.default('none'),
      bearerSecret: mcpServerFields.bearerSecret.default(null),
      envSecrets: mcpServerFields.envSecrets.default({}),
      oauthScope: mcpServerFields.oauthScope.default(null),
      timeoutMs: mcpServerFields.timeoutMs.default(30_000),
      enabled: mcpServerFields.enabled.default(true),
    })
    .strict(),
  output: McpServer,
  summary: 'Registers an external MCP server for in-app agents, over http or as a stdio package from the image (CO-0838)',
  description:
    'http needs url; stdio needs package (one of MCP_STDIO_PACKAGES). auth bearer needs bearerSecret; auth oauth is linked afterwards with agent.mcpServer.connect in the web app. ' +
    'Then read its tools with agent.mcpServer.sync and turn on the ones agents may use.',
});

export const updateMcpServer = defineAction({
  name: 'agent.mcpServer.update',
  method: 'patch',
  path: '/agent/mcp-servers/{id}',
  permission: P.keys.manage,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      expectedVersion: ExpectedVersion,
      title: mcpServerFields.title.optional(),
      url: mcpServerFields.url.optional(),
      package: mcpServerFields.package.optional(),
      auth: mcpServerFields.auth.optional(),
      bearerSecret: mcpServerFields.bearerSecret.optional(),
      envSecrets: mcpServerFields.envSecrets.optional(),
      oauthScope: mcpServerFields.oauthScope.optional(),
      timeoutMs: mcpServerFields.timeoutMs.optional(),
      enabled: mcpServerFields.enabled.optional(),
      tools: z
        .array(z.object({ name: z.string().max(100), enabled: z.boolean().optional(), mode: z.enum(MCP_TOOL_MODES).optional() }).strict())
        .max(500)
        .optional()
        .describe('Turn tools on or off and set read or write, by name; tools left out stay as they are'),
    })
    .strict(),
  output: McpServer,
  summary: 'Changes an external MCP server, or which of its tools agents may use and whether they wait for a person (CO-0838)',
});

export const deleteMcpServer = defineAction({
  name: 'agent.mcpServer.delete',
  method: 'delete',
  path: '/agent/mcp-servers/{id}',
  permission: P.keys.manage,
  successStatus: 200,
  kind: 'destructive',
  input: z.object({ id: Id, expectedVersion: QueryVersion }),
  output: z.object({ deleted: z.boolean() }),
  summary: 'Removes an external MCP server; agents lose its tools at their next run (CO-0838)',
});

export const syncMcpServer = defineAction({
  name: 'agent.mcpServer.sync',
  method: 'post',
  path: '/agent/mcp-servers/{id}/sync',
  permission: P.keys.manage,
  successStatus: 200,
  input: z.object({ id: Id }),
  output: McpServer,
  summary: 'Reads the tools of an external MCP server: new tools come in off, gone tools leave, the choices on the others stay (CO-0838)',
});

export const connectMcpServer = defineAction({
  name: 'agent.mcpServer.connect',
  method: 'post',
  path: '/agent/mcp-servers/{id}/connect',
  permission: P.keys.manage,
  successStatus: 200,
  input: z.object({ id: Id }),
  output: z.object({ authorizeUrl: z.string().nullable().describe('Where the admin logs in at the server; null: the tokens already work') }),
  // Only for a person in the web app: the login happens in their browser.
  restOnly: true,
  summary: 'Starts the OAuth link of an external MCP server: discovery, client registration and PKCE (CO-0838)',
});

export const mcpServerCallback = defineAction({
  name: 'agent.mcpServer.callback',
  method: 'post',
  path: '/agent/mcp-servers/{id}/callback',
  permission: P.keys.manage,
  successStatus: 200,
  input: z.object({ id: Id, code: z.string().max(2000).optional(), state: z.string().max(200).optional(), error: z.string().max(200).optional(), error_description: z.string().max(1000).optional() }),
  output: z.object({ linked: z.boolean() }),
  restOnly: true,
  summary: 'Finishes the OAuth link of an external MCP server with what the login gave back on /admin/connections?mcpServer={id} (CO-0838)',
});

export const agentActions = [
  listAgents,
  getAgent,
  agentProfile,
  createAgent,
  updateAgent,
  pauseAgent,
  pauseAllAgents,
  resumeAgent,
  terminateAgent,
  listRuns,
  getRun,
  exportFeedback,
  summarizeCosts,
  getBudget,
  setBudget,
  resetBudget,
  listRoutines,
  getRoutine,
  createRoutine,
  updateRoutine,
  pauseRoutine,
  resumeRoutine,
  runRoutine,
  steerRun,
  listRoutineEvents,
  claimTask,
  listTasks,
  getTask,
  heartbeatTask,
  addEvidence,
  submitTask,
  reviewTask,
  releaseTask,
  scheduleWake,
  listWakes,
  listThreads,
  getThread,
  updateThread,
  sendMessage,
  listMessages,
  startSession,
  cancelRun,
  listBudgets,
  capBudget,
  listDefinitions,
  taskStatus,
  getCockpit,
  agentTimeline,
  listRevisions,
  revertAgent,
  exportAgent,
  importAgent,
  registerWorker,
  workerHeartbeat,
  nextSession,
  sessionEvents,
  finishSession,
  listWorkers,
  setForgejoToken,
  shareForgejoBot,
  listMcpServers,
  getMcpServer,
  createMcpServer,
  updateMcpServer,
  deleteMcpServer,
  syncMcpServer,
  connectMcpServer,
  mcpServerCallback,
];
